import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlOptionsSharesRegistration, GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { GenericError } from '@startuptools/ui/Error';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { Typography } from '@startuptools/ui/Typography';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { toDateTimeMaybe } from '@startuptools/common/common';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { Alert } from '@startuptools/ui/Alert';
import { useOsrSetRecordDayMutation } from '../../../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';

export const OsrStepRecordDayNgWrap = ({ osrId, warrantProgramId }: { osrId: string; warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId, getWpErrors: true } });
  const osr = warrantProgram.optionsSharesRegistrations.find(osr => osr.id === osrId);
  if (!osr) {
    return <GenericError />;
  }

  return <OsrStepRecordDay osr={osr} wp={warrantProgram} />;
};

type FormValues = {
  recordDay: DateTime;
};

export const OsrStepRecordDay = ({
  osr,
  wp,
}: {
  osr: Pick<GqlOptionsSharesRegistration, 'id' | 'recordDay' | 'payBeforeDate'>;
  wp: Pick<GqlWarrantProgram, 'id' | 'shareSubscribeStartDate' | 'companyId'>;
}) => {
  const { stepNext } = useStepper();
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { recordDay: toDateTimeMaybe(osr.recordDay) ?? toDateTimeMaybe(osr.payBeforeDate) ?? undefined },
  });

  const [doSubmit, { loading }] = useOsrSetRecordDayMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({
        variables: {
          companyId: wp.companyId,
          input: { id: osr.id, recordDay: values.recordDay.toUTC(0, { keepLocalTime: true }).toISO()! },
        },
      });
      return stepNext();
    } catch (e) {
      genericErrorToast();
      captureException(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Record Date" />
        </Typography>

        <Alert>
          <Trans id="Here you enter the date for when the shares are subscribed for and entered into the share register." />
        </Alert>

        <Controller
          control={control}
          name="recordDay"
          render={({ field }) => (
            <FormControl sx={{ maxWidth: 300 }}>
              <FormLabel>
                <Trans id="Record Date" />
              </FormLabel>
              <DateInput {...field} maxDate={toDateTimeMaybe(osr.payBeforeDate)?.startOf('day')} />
            </FormControl>
          )}
        />
        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
